import { useEffect, useRef, useState, type ReactNode } from 'react';
import { BACKUP_MODES, type BackupDto, type BackupMode, type ConnectionDto } from '@visualitics-toolkit/shared';

import { Button, Field, Input, Select } from '../atoms';
import { ErrorBlock, OrgTypeahead } from '../molecules';
import { api } from '../../lib/api-client';
import { useStartBackup } from '../../lib/queries';
import { Dialog } from './Dialog';

const MODE_LABELS: Record<BackupMode, string> = {
  full: 'Completo (pg_dump --format=custom)',
  'schema-only': 'Somente schema (pg_dump --schema-only)',
  organization: 'Por organização (COPY filtrado)',
};

export interface BackupProgressEvent {
  backupId: string;
  pct: number;
  message: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  errorMessage?: string | null;
}

export function BackupDialog({
  connection,
  organizationId: initialOrganizationId,
  onClose,
}: {
  connection: ConnectionDto;
  organizationId?: string;
  onClose: () => void;
}): ReactNode {
  const [mode, setMode] = useState<BackupMode>('full');
  const [organizationId, setOrganizationId] = useState(initialOrganizationId ?? '');
  const [note, setNote] = useState('');
  const [backup, setBackup] = useState<BackupDto | null>(null);
  const progress = useBackupProgress(backup?.id);
  const start = useStartBackup();

  const finished = progress?.status === 'completed' || progress?.status === 'failed';

  return (
    <Dialog title="Backup do banco alvo" onClose={onClose}>
      {!backup ? (
        <div className="space-y-1">
          <p className="mb-3 text-sm text-muted">
            Conexão: <strong className="text-content">{connection.name}</strong> ·{' '}
            {connection.host}:{connection.port}/{connection.database}
          </p>

          <Field label="Modo">
            <Select value={mode} onChange={(event) => setMode(event.target.value as BackupMode)}>
              {BACKUP_MODES.map((value) => (
                <option key={value} value={value}>
                  {MODE_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>

          {mode === 'organization' && (
            <Field
              label="Organização"
              help="pg_dump não filtra linhas; este modo usa COPY sobre o inventário dinâmico."
            >
              <OrgTypeahead value={organizationId} onChange={setOrganizationId} />
            </Field>
          )}

          <Field label="Observação" help="Fica registrada junto do backup.">
            <Input value={note} onChange={(event) => setNote(event.target.value)} />
          </Field>

          {start.error instanceof Error && <ErrorBlock message={start.error.message} />}

          <div className="mt-4 flex justify-end gap-2">
            <Button onClick={onClose}>Cancelar</Button>
            <Button
              variant="primary"
              loading={start.isPending}
              disabled={mode === 'organization' && !organizationId}
              onClick={() =>
                start.mutate(
                  {
                    connectionId: connection.id,
                    mode,
                    organizationId: mode === 'organization' ? organizationId : undefined,
                    note: note || undefined,
                  },
                  { onSuccess: setBackup },
                )
              }
            >
              Iniciar backup
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3" aria-live="polite">
          <p className="text-sm text-content">{progress?.message ?? 'Na fila…'}</p>
          <div className="h-2 w-full overflow-hidden rounded-full bg-surface">
            <div
              className="h-full rounded-full bg-accent transition-all"
              style={{ width: `${progress?.pct ?? backup.progressPct}%` }}
              role="progressbar"
              aria-valuenow={progress?.pct ?? backup.progressPct}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>

          {progress?.status === 'failed' && (
            <ErrorBlock className="break-words" message={failureDetail(progress)} />
          )}

          <div className="flex justify-end gap-2">
            {progress?.status === 'completed' && (
              <a
                className="inline-flex items-center rounded-md border border-border px-3 py-2 text-sm hover:border-accent"
                href={`/api/v1/backups/${backup.id}/download`}
              >
                Baixar arquivo
              </a>
            )}
            <Button variant={finished ? 'primary' : 'secondary'} onClick={onClose}>
              {finished ? 'Fechar' : 'Fechar (continua em background)'}
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

/** Progresso ao vivo via SSE — dump completo leva minutos e o operador precisa ver avanço. */
export function useBackupProgress(backupId?: string): BackupProgressEvent | null {
  const [progress, setProgress] = useState<BackupProgressEvent | null>(null);
  const sourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!backupId) return;

    const source = new EventSource(`/api/v1/backups/${backupId}/events`, {
      withCredentials: true,
    });
    sourceRef.current = source;

    source.onmessage = (event: MessageEvent<string>) => {
      const parsed = JSON.parse(event.data) as BackupProgressEvent;
      setProgress(parsed);
      if (parsed.status === 'completed' || parsed.status === 'failed') source.close();
    };
    source.onerror = () => {
      source.close();
      void api
        .get<BackupDto>(`/backups/${backupId}`)
        .then((dto) => {
          setProgress((current) => {
            if (current?.status === 'completed' || current?.status === 'failed') return current;
            return {
              backupId: dto.id,
              pct: dto.progressPct,
              message: dto.message ?? '',
              status: dto.status,
              errorMessage: dto.errorMessage,
            };
          });
        })
        .catch(() => undefined);
    };

    return () => {
      source.close();
      sourceRef.current = null;
    };
  }, [backupId]);

  return progress;
}

function failureDetail(progress: BackupProgressEvent): string {
  const detail = progress.errorMessage?.trim();
  if (detail) return detail;
  const message = progress.message.trim();
  if (message && message !== 'Falhou') return message;
  return 'Falha sem detalhes. Consulte os logs da aplicação (evento backup.failed).';
}
